import { graphql } from 'app/generated/gql'
import type {
  ActionCenterCreateHoldMutation,
  ActionCenterHoldsQuery,
  ActionCenterWakeHoldMutation,
} from 'app/generated/gql/graphql'
import { gqlMutation, gqlQuery } from 'app/lib/graphqlClient'
import { logError } from 'lib/logging'
import type { Dismissal } from './responseQueue'
import type { SnoozeEntry } from './momentumStore'

/**
 * The server-side hold behind every snooze and every done (TRK-5599): the
 * API's Action model, keyed `actionId = queueName:queueId`. The client always
 * writes `queueId = contactId`, making a hold per-contact-per-surface, and a
 * second create on the pair replaces the first. `workspaceId`/`orgId` are
 * session-derived, so one person's hold is the whole practice's.
 *
 * The model has no `since` field, so "any inbound newer than the hold ends
 * it" is judged against `updatedDate`, which moves on every state change.
 */

/** One hit of the holds query below, so the selection can never drift from the type. */
export type QueueHold = ActionCenterHoldsQuery[`actions`][`hits`][number]

/** The vocabulary. The API stores a bare string; the UI never writes anything else. */
export const HOLD_STATES = {
  /** Handled. Applies until the contact sends something new. */
  done: `done`,
  /** Deferred to a date. A reply still ends it early. */
  snoozed: `snoozed`,
  /** Ended early by a person. Kept, not deleted, so the record survives. */
  woken: `woken`,
} as const

/** The response queue's holds live under this name; boards use their queue ids. */
export const RESPONSE_QUEUE_NAME = `response`

export const HOLDS_QUERY = graphql(`
  query ActionCenterHolds($search: String, $pagination: PaginationInput) {
    actions(search: $search, pagination: $pagination) {
      total
      hits {
        actionId
        queueName
        queueId
        contactId
        state
        untilDate
        note
        userId
        version
        createdDate
        updatedDate
        orgId
        workspaceId
      }
    }
  }
`)

export const CREATE_HOLD_MUTATION = graphql(`
  mutation ActionCenterCreateHold($input: CreateActionInput!) {
    createAction(input: $input) {
      actionId
      queueName
      queueId
      contactId
      state
      untilDate
      note
      userId
      version
      createdDate
      updatedDate
      orgId
      workspaceId
    }
  }
`)

export const WAKE_HOLD_MUTATION = graphql(`
  mutation ActionCenterWakeHold($actionId: ID!, $input: UpdateActionInput!) {
    updateAction(actionId: $actionId, input: $input) {
      actionId
      queueName
      queueId
      contactId
      state
      untilDate
      note
      userId
      version
      createdDate
      updatedDate
      orgId
      workspaceId
    }
  }
`)

/**
 * The workspace's live holds, newest state only (the API's list endpoint
 * already filters to the latest version of each pair).
 *
 * One read per page load is the design: a hold can expire by being read, so
 * no job has to run anywhere for a dated snooze to end on time.
 */
export async function fetchHolds(search?: string): Promise<QueueHold[]> {
  const result = await gqlQuery<ActionCenterHoldsQuery>(HOLDS_QUERY, {
    search,
    pagination: { limit: 1000 },
  })
  if (result.error) throw result.error
  return result.data?.actions.hits ?? []
}

/**
 * Set (or replace) the hold on one row on one surface. `contactId` is the
 * row's key there: the contact on the boards, the row `id` (a thread, for a
 * text) on the response queue.
 */
export async function saveHold(input: {
  contactId: string
  queueName: string
  state: string
  untilDate?: string
  note?: string
}): Promise<void> {
  const result = await gqlMutation(CREATE_HOLD_MUTATION, {
    input: {
      contactId: input.contactId,
      queueName: input.queueName,
      // The contact IS the queue-scoped key. See the module note.
      queueId: input.contactId,
      state: input.state,
      untilDate: input.untilDate,
      note: input.note,
    },
  })
  if (result.error) {
    // The optimistic UI already moved on; the poll or the next load restores
    // the truth. Losing a snooze is recoverable, hiding the failure is not.
    logError(`action-center`, `Could not save the hold for ${input.queueName}`, result.error)
    throw result.error
  }
}

/**
 * End a hold early, for everybody.
 *
 * An update to `woken` rather than a delete: the ticket wants every hold to
 * stay readable back, and the list still returns the latest state, so the UI
 * filters `woken` out rather than the record disappearing.
 */
export async function wakeHold(queueName: string, contactId: string): Promise<void> {
  const result = await gqlMutation(WAKE_HOLD_MUTATION, {
    actionId: `${queueName}:${contactId}`,
    input: { state: HOLD_STATES.woken },
  })
  if (result.error) {
    logError(`action-center`, `Could not wake the hold for ${queueName}`, result.error)
    throw result.error
  }
}

/**
 * A hold as the response queue's dismissal record.
 *
 * `latest` carries the hold's own state-change instant, so `isDismissed`'s
 * existing rule "a newer touch revives the row" becomes "any inbound after
 * the hold was set ends it", which is the ticket's wake rule.
 */
export function holdToDismissal(hold: QueueHold): Dismissal | undefined {
  if (hold.state !== HOLD_STATES.done && hold.state !== HOLD_STATES.snoozed) return undefined
  return { latest: hold.updatedDate, until: hold.untilDate ?? undefined }
}

/** The response queue's holds, keyed the way its dismissal map is keyed. */
export function holdsToDismissals(holds: QueueHold[]): Record<string, Dismissal> {
  const map: Record<string, Dismissal> = {}
  for (const hold of holds) {
    if (hold.queueName !== RESPONSE_QUEUE_NAME) continue
    const record = holdToDismissal(hold)
    if (record) map[hold.contactId] = record
  }
  return map
}

/**
 * A hold as a board snooze entry.
 *
 * Note the mapping: the local store always called the queue's id `queueId`,
 * while the API files it under `queueName` (its own `queueId` is the contact,
 * see the module note). This is the one place the two vocabularies meet.
 */
export function holdToSnoozeEntry(hold: QueueHold): SnoozeEntry | undefined {
  if (hold.state !== HOLD_STATES.snoozed || !hold.untilDate) return undefined
  if (hold.queueName === RESPONSE_QUEUE_NAME) return undefined
  return { contactId: hold.contactId, queueId: hold.queueName, until: hold.untilDate, byUserId: hold.userId }
}

export function holdsToSnoozeEntries(holds: QueueHold[]): SnoozeEntry[] {
  return holds.flatMap((hold) => {
    const entry = holdToSnoozeEntry(hold)
    return entry ? [entry] : []
  })
}
